import {
  ColorIcon,
  colors,
  customEvent,
  EventPathType,
  getCdnImageSrc,
  HLine,
  HStack,
  Spacing,
  useAlertDialog,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import { useOverlay } from "@toss/use-overlay";
import { useAtomValue } from "jotai";
import { OverlayModal } from "goi_common/src/components/OverlayModal";

import { ObituaryVisitorVersionResponse } from "@/apis/obituaries";
import { BANK_CODE_REVERSE } from "@/containers/BankAccountContainer/constants/bankCode";
import { BugoShareChoiceModalContent } from "@/components/Modals/BugoShareChoiceModalContent";

import { portraitPhotoAtom } from "../../atoms";
import {
  FLOWER_ORDER_DISABLED_MESSAGE,
  FlowerOrderDisabledReason,
  getFlowerOrderDisabledReason,
  isTargetObituaryForVisitorFixedFooterVariant,
} from "../../utils";
import { useGetObituary } from "../hooks/useGetObituary";

interface BugoIntroSectionProps {
  readOnly?: boolean;
  isVisitorType?: boolean;
  path: EventPathType;
}

export const BugoIntroSection = ({
  readOnly = false,
  isVisitorType = false,
  path,
}: BugoIntroSectionProps) => {
  const router = useRouter();
  const { obituaryKey } = router.query as { obituaryKey?: string };
  const { data } = useGetObituary();
  const portraitPhoto = useAtomValue(portraitPhotoAtom);

  const isSomeValidValue =
    data?.age || data?.name || data?.passed_away_on || data?.die_term;

  const flowerOrderDisabledReason = getFlowerOrderDisabledReason(data);

  return (
    <VStack
      css={{
        position: "relative",
        marginBottom: isVisitorType ? "40px" : undefined,
      }}
    >
      <VStack
        css={{
          backgroundImage: `url(${getCdnImageSrc(
            "bugo/form/flower_background.jpg"
          )})`,
          backgroundSize: "cover",
          // height: "400px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spacing gap={48} />
        <HStack alignItems="center" justifyContent="center">
          <ColorIcon name="icon-bugo-flower" size={35} color="" />
          <span
            css={{
              fontFamily: "Noto Serif KR",
              fontSize: "40px",
              lineHeight: "60px",
              fontWeight: "500",
              color: colors.white,
            }}
          >
            訃告
          </span>
          <ColorIcon
            css={{
              transform: "scaleX(-1)",
            }}
            name="icon-bugo-flower"
            size={35}
            color=""
          />
        </HStack>
        <Spacing gap={28} />
        <VStack
          gap="5px"
          css={{
            fontFamily: "Nanum Myeongjo",
            fontSize: "18px",
            // fontWeight: "400",
            textAlign: "center",
            color: colors.warmGray150,
            lineHeight: "32px",
            letterSpacing: "-0.4px",
          }}
        >
          <div
            css={{
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "34px",
            }}
          >
            <span
              css={{
                fontFamily: "Noto Serif KR",
              }}
            >
              故
            </span>{" "}
            {data?.name ? data.name : "____"}
            <span
              css={{
                fontWeight: "400",
              }}
            >
              님께서
            </span>
            {data?.age != null && data?.is_age_public !== false && (
              <span
                css={{
                  fontWeight: "400",
                  marginLeft: "6px",
                }}
              >
                (향년 {data.age}세)
              </span>
            )}
          </div>

          {(data?.religious_position ||
            data?.baptismal_name ||
            data?.religion === "불교") && (
            <div
              css={{
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "22px",
                color: colors.warmGray300,
                letterSpacing: "0px",
              }}
            >
              {data?.religion === "불교"
                ? ["불교", data.religious_position].filter(Boolean).join(" · ")
                : [data.religious_position, data.baptismal_name]
                    .filter(Boolean)
                    .join(" · ")}
            </div>
          )}

          <div>
            {data?.passed_away_on
              ? dayjs(data.passed_away_on).format("YYYY년 MM월 DD일")
              : "____년 _월 _일"}{" "}
            {data?.die_term ? data.die_term : "별세"}
            {"하셨음을"}
          </div>

          <div>{"삼가 알려드립니다."}</div>
        </VStack>
        {portraitPhoto && (
          <>
            <Spacing gap={24} />
            <div
              css={{
                display: "inline-flex",
                overflow: "hidden",
                borderRadius: "5px",
                border: "1.5px solid #FFF",
                background: "#FFF",
                boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.12)",
              }}
            >
              <img
                src={portraitPhoto}
                alt="영정사진"
                css={{
                  width: "120px",
                  height: "160px",
                  objectFit: "cover",
                }}
              />
            </div>
          </>
        )}
        <Spacing
          gap={(() => {
            if (!portraitPhoto) return 72;
            if (isVisitorType) return 80;
            return 40;
          })()}
        />
        {!readOnly && (
          <>
            <HStack
              as="button"
              gap="4px"
              alignItems="center"
              justifyContent="center"
              className="button_16_sb white"
              css={{
                width: "fit-content",
                padding: "12px 16px",
                borderRadius: "20px",
                border: `1px solid ${colors.gray500}`,
              }}
              onClick={() => {
                customEvent({
                  path,
                  trigger_and_target: "goin_button",
                });
                router.push(
                  obituaryKey
                    ? `/bugo/update/goin?obituaryKey=${obituaryKey}`
                    : "/bugo/update/goin/"
                );
              }}
            >
              <ColorIcon name="icon-pencil" size="16px" color="" />
              {isSomeValidValue ? "정보 수정하기" : "정보 입력하기"}
            </HStack>
            <Spacing gap={40} />
          </>
        )}
      </VStack>
      {/*
        Target 부고(박순자님 등 특정 부고)에서는 이 중앙 3-버튼 박스를 숨기고
        하단 Fixed 3-버튼 배치로 대체 (기존 사용자 컴플레인 대응).
      */}
      {isVisitorType && !isTargetObituaryForVisitorFixedFooterVariant(data) && (
        <VisitorActionBox
          path="visitor"
          bank_accounts={data?.bank_accounts}
          flowerOrderDisabledReason={flowerOrderDisabledReason}
        />
      )}
    </VStack>
  );
};

interface VisitorActionBoxProps {
  bank_accounts?: ObituaryVisitorVersionResponse["bank_accounts"] | null;
  flowerOrderDisabledReason: FlowerOrderDisabledReason;
  path: EventPathType;
}

const VisitorActionBox = ({
  path,
  bank_accounts,
  flowerOrderDisabledReason,
}: VisitorActionBoxProps) => {
  const isEnableFlowerOrder = flowerOrderDisabledReason === null;
  const overlay = useOverlay();

  const onClickBankAccount = () => {
    customEvent({
      path,
      trigger_and_target: "bank_account_button",
    });

    if (!bank_accounts?.length) {
      openAlertDialog({
        content: "등록된 부의금 계좌가 없습니다.",
        buttonText: "확인",
      });
      return;
    }

    overlay.open(({ isOpen, close: onClose }) => (
      <OverlayModal
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={onClose}
        modalContentStyle={{
          maxWidth: "300px",
          borderRadius: "20px",
        }}
        overlayModalContent={
          <BankAccountModalContent bankAccounts={bank_accounts} />
        }
      />
    ));
  };

  const onClickShareVisitorVersion = () => {
    customEvent({
      path,
      trigger_and_target: "share_button",
    });

    overlay.open(({ isOpen, close: onClose }) => (
      <OverlayModal
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={onClose}
        modalContentStyle={{
          maxWidth: "300px",
          borderRadius: "20px",
        }}
        overlayModalContent={
          <BugoShareChoiceModalContent onClose={onClose} type="visitor" />
        }
      />
    ));
  };

  const router = useRouter();
  const { sangjuKey } = router.query as { sangjuKey?: string };

  const { openAlertDialog } = useAlertDialog();

  const onClickFlowerOrderActionBox = () => {
    customEvent({
      path,
      trigger_and_target: "flower_order_box_button",
    });

    if (isEnableFlowerOrder) {
      router.push(`/flower/select?sangjuKey=${sangjuKey}`);
      return;
    }
    openAlertDialog({
      content: FLOWER_ORDER_DISABLED_MESSAGE[flowerOrderDisabledReason!],
      buttonText: "확인",
    });
  };

  const buttonCSS = {
    minWidth: "120px",
    height: "100%",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "24px",
  };

  return (
    <HStack
      alignItems="center"
      css={{
        position: "absolute",
        height: "100px",
        bottom: "-50px",
        left: "50%",
        transform: "translateX(-50%)",
        whiteSpace: "pre-wrap",
        fontFamily: "Nanum Myeongjo",
        borderRadius: "10px",
        background: colors.white,
        boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      <button css={buttonCSS} onClick={onClickBankAccount}>
        {"부의금\n보내기"}
      </button>
      <HLine height="28px" width="1px" color={colors.gray300} />
      <button
        css={
          isEnableFlowerOrder
            ? {
                ...buttonCSS,
                fontWeight: "700",
                background:
                  "linear-gradient(180deg, #FF5A5A 0%, #E8BA48 55.18%, #B18A28 99.5%)",
                backgroundClip: "text",
                webkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }
            : {
                ...buttonCSS,
                fontWeight: "600",
                color: colors.gray500,
              }
        }
        onClick={onClickFlowerOrderActionBox}
      >
        {"근조화환\n보내기"}
      </button>
      <HLine height="28px" width="1px" color={colors.gray300} />
      <button css={buttonCSS} onClick={onClickShareVisitorVersion}>
        {"부고\n전달하기"}
      </button>
    </HStack>
  );
};

export const BankAccountModalContent = ({
  bankAccounts,
}: {
  bankAccounts:
    | ObituaryVisitorVersionResponse["bank_accounts"]
    | null
    | undefined;
}) => {
  const { openAlertDialog } = useAlertDialog();

  return (
    <VStack
      background={colors.white}
      css={{
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <VStack
        gap="2px"
        padding="28px 0px 32px 0px"
        justifyContent="center"
        css={{
          whiteSpace: "pre-wrap",
          textAlign: "center",
        }}
      >
        <div className="subtitle_21_sb gray800">부의금 계좌</div>
        <div className="gray500">직접 송금해주셔야 하는 서비스입니다.</div>
      </VStack>
      <HLine color={colors.gray100} />

      <div>
        <VStack
          css={{
            maxHeight: "278px",
            minHeight: "278px",
            overflow: "auto",
          }}
        >
          {bankAccounts && bankAccounts?.length < 1 && (
            <HStack
              className="body3_m gray800"
              justifyContent="center"
              padding="100px 5px"
              css={{
                textAlign: "center",
              }}
            >
              계좌번호를 등록한
              <br />
              상주분이 없습니다.
            </HStack>
          )}
          {bankAccounts &&
            bankAccounts?.length > 0 &&
            bankAccounts?.map(({ owner, bank, number }, index) => {
              return (
                <HStack
                  key={`${owner}-bank-${bank}-${number}-${index}`}
                  justifyContent="space-between"
                  alignItems="flex-end"
                  css={{
                    margin: "20px 24px 0px 24px",
                    paddingBottom: "16px",
                    borderBottom: `1px solid ${colors.gray100}`,
                  }}
                >
                  <VStack>
                    <div className="body3_m brown350">예금주</div>
                    <div className="body3_r">
                      {owner} ({BANK_CODE_REVERSE[bank]})
                    </div>
                    <div className="body3_r">{number}</div>
                  </VStack>
                  <HStack
                    as="button"
                    padding="4px 10px"
                    gap="2px"
                    background={colors.warmGray750}
                    borderRadius="5px"
                    onClick={() => {
                      navigator.clipboard.writeText(number).then(() => {
                        openAlertDialog({
                          content: "복사되었습니다.",
                          buttonText: "확인",
                        });
                      });
                    }}
                  >
                    <ColorIcon name="icon-copy" color="" />
                    <div className="caption_12_sb white">복사</div>
                  </HStack>
                </HStack>
              );
            })}
        </VStack>
      </div>
    </VStack>
  );
};
